import { z } from 'zod';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { MyFocusAreaDefinition } from 'libs/end-to-end/src/my-focus-area-definition';
import { MyMetricDefinition } from 'libs/end-to-end/src/my-metric-definition';
import upperFirst from 'lodash/upperFirst';
import { MetricDefinitionCalibrationFraming } from 'libs/end-to-end/src/types/databases/metrics/metric-definition-calibration-framing';
import { MetricDisplayModifier } from 'libs/end-to-end/src/metrics';
import { capitalizeFirstLetterInEachSentence } from 'libs/end-to-end/src/string-formatting';
import { CalibrationFraming } from 'libs/end-to-end/src/calibration-framing';

export const RootCauseAnalysisSection = z
  .object({
    focusArea: FocusArea,
    subSections: z.array(
      z.object({
        demographic: z.string(),
        bulletPoints: z.array(z.string()),
      }),
    ),
  })
  .describe('RootCauseAnalysisSection');

export type RootCauseAnalysisSection = z.infer<typeof RootCauseAnalysisSection>;

export const RootCauseAnalysis = z
  .object({
    sections: z.array(RootCauseAnalysisSection),
  })
  .describe('RootCauseAnalysis');

export type RootCauseAnalysis = z.infer<typeof RootCauseAnalysis>;

export const RootCauseBinScatterGroupTrendline = z
  .object({
    X0: z.number(),
    X1: z.number(),
    Y0: z.number(),
    Y1: z.number(),
    slope: z.number(),
  })
  .describe('RootCauseBinScatterGroupTrendline');

export type RootCauseBinScatterGroupTrendline = z.infer<
  typeof RootCauseBinScatterGroupTrendline
>;

export const calculateSlopeForTrendline = (
  trendline: RootCauseBinScatterGroupTrendline,
): number | 'horizontal' | 'vertical' | 'indeterminate' => {
  return calculateSlope({
    y1: trendline.Y1,
    y0: trendline.Y0,
    x1: trendline.X1,
    x0: trendline.X0,
  });
};

export const calculateSlope = ({
  y1,
  y0,
  x1,
  x0,
}: {
  y1: number;
  y0: number;
  x1: number;
  x0: number;
}): number | 'vertical' | 'horizontal' | 'indeterminate' => {
  const slope = (y1 - y0) / (x1 - x0);
  if (slope === 0) {
    return 'horizontal';
  } else if (isNaN(slope) || slope === Infinity || slope === -Infinity) {
    return y1 === y0 && x1 === x0 ? 'indeterminate' : 'vertical';
  } else {
    return slope;
  }
};

export const RootCauseBinScatterGroup = z
  .object({
    name: z.string(),
    trendline: RootCauseBinScatterGroupTrendline,
  })
  .describe('RootCauseBinScatterGroup');

export type RootCauseBinScatterGroup = z.infer<typeof RootCauseBinScatterGroup>;

export const RootCauseGeneratedTextOutputVariableToOutputMap = z
  .record(z.string(), z.string())
  .describe('RootCauseGeneratedTextOutputVariableToOutputMap');

export type RootCauseGeneratedTextOutputVariableToOutputMap = z.infer<
  typeof RootCauseGeneratedTextOutputVariableToOutputMap
>;

export const FailStatusType = z
  .enum(['BiasAgainst', 'BiasInFavorOf'])
  .describe('FailStatusType');

export type FailStatusType = z.infer<typeof FailStatusType>;

export const RootCauseVisualizationAttributes = z
  .object({
    focusArea: FocusArea,
    metric: z.string(),
    displayAsPercentage: z.boolean(),
    taskMetric: z.string(),
    taskMetricDisplayName: z.string(),
    taskMetricDisplayModifier: MetricDisplayModifier.nullish(),
    failStatusType: FailStatusType,
    framing: MetricDefinitionCalibrationFraming,
    skillFraming: MetricDefinitionCalibrationFraming,
    dimension: z.string(),
    treatmentLevel: z.string(),
    referenceLevel: z.string(),
  })
  .describe('RootCauseVisualizationAttributes');

export type RootCauseVisualizationAttributes = z.infer<
  typeof RootCauseVisualizationAttributes
>;

export const RootCauseGeneratedTextOutputs = z
  .object({
    analysisType: z.string(),
    biasTestId: z.number().optional(), // See SensitveBiasTestIds
    diff: z.number(),
    controlVariable: z.string(),
    focusArea: FocusArea.optional(),
    treatmentLevel: z.string(),
    referenceLevel: z.string(),
    variableToOutputMap: RootCauseGeneratedTextOutputVariableToOutputMap,
  })
  .describe('RootCauseGeneratedTextOutputs');

export type RootCauseGeneratedTextOutputs = z.infer<
  typeof RootCauseGeneratedTextOutputs
>;

export const RootCauseBinScatterControlledVsUncontrolledGroupResult = z
  .object({
    plot_type: z.literal('BinScatterControlledVsUncontrolled'),
    control_variable: z.string(),
    beta_difference: z.number().nullish(),
    disparity_difference: z.number().nullish(),
    treatment_level: RootCauseBinScatterGroup,
    reference_level: RootCauseBinScatterGroup,
    explanation: RootCauseGeneratedTextOutputs,
    visualizationAttributes: RootCauseVisualizationAttributes.nullish(),
  })
  .describe('RootCauseBinScatterControlledVsUncontrolledGroupResult');

export type RootCauseBinScatterControlledVsUncontrolledGroupResult = z.infer<
  typeof RootCauseBinScatterControlledVsUncontrolledGroupResult
>;

export const RootCauseGraphType = z
  .enum([
    'DivergedBarGraph',
    'BinScatterControlledVsUncontrolled',
    'GenericBarGraph',
    'TextOnly',
    'BoxPlot',
  ])
  .describe('RootCauseGraphType');

export type RootCauseGraphType = z.infer<typeof RootCauseGraphType>;

export const RootCauseDivergedBarGraphGroupResult = z
  .object({
    plot_type: z.literal('DivergedBarGraph'),
    control_variable: z.string(),
    not_controlled_disparity: z.number(),
    controlled_disparity: z.number(),
    pct_difference: z.number().optional(),
    disparity_difference: z.number().nullish(),
    text: RootCauseGeneratedTextOutputs.nullish(),
    visualizationAttributes: RootCauseVisualizationAttributes.nullish(),
  })
  .describe('RootCauseDivergedBarGraphGroupResult');

// test
export type RootCauseDivergedBarGraphGroupResult = z.infer<
  typeof RootCauseDivergedBarGraphGroupResult
>;

export const RootCauseGenericBarGraphGroupResult = z
  .object({
    plot_type: z.literal('GenericBarGraph'),
    control_variable: z.string(),
    x: z.array(z.string()),
    y: z.array(z.number()),
    disparity_difference: z.number().nullish(),
    text: z.union([z.string(), RootCauseGeneratedTextOutputs]).nullish(),
    explanation: z.union([z.string(), RootCauseGeneratedTextOutputs]).nullish(),
    visualizationAttributes: RootCauseVisualizationAttributes.nullish(),
  })
  .describe('RootCauseGenericBarGraphGroupResult');

export type RootCauseGenericBarGraphGroupResult = z.infer<
  typeof RootCauseGenericBarGraphGroupResult
>;

export const RootCauseGraphVariableBarPair = z
  .object({
    name: z.string(),
    reference_level: z.number(),
    treatment_level: z.number(),
  })
  .describe('RootCauseGraphVariableBarPair');

export type RootCauseGraphVariableBarPair = z.infer<
  typeof RootCauseGraphVariableBarPair
>;

export const RootCauseAddingVariablesGraphPlotData = z
  .object({
    decompositionType: z.union([
      z.literal('Adding Variables'),
      z.literal('Oaxaca X'),
    ]),
    treatmentDimensionLevelColumnValue: z.string(),
    referenceDimensionLevelColumnValue: z.string(),
    withVariableBarPair: RootCauseGraphVariableBarPair,
    withoutVariableBarPair: RootCauseGraphVariableBarPair,
    originalTreatmentLevelDisparity: z.number(),
    decomposedTreatmentLevelDisparity: z.number(),
    disparityDelta: z.number(),
  })
  .describe('RootCauseAddingVariablesGraphPlotData');

export type RootCauseAddingVariablesGraphPlotData = z.infer<
  typeof RootCauseAddingVariablesGraphPlotData
>;

export const RootCauseOaxacaBetaGraphGroupData = z
  .object({
    referenceCategoryBarPair: RootCauseGraphVariableBarPair,
    treatmentCategoryBarPair: RootCauseGraphVariableBarPair,
  })
  .describe('RootCauseOaxacaBetaGraphGroupData');

export type RootCauseOaxacaBetaGraphGroupData = z.infer<
  typeof RootCauseOaxacaBetaGraphGroupData
>;

export const RootCauseOaxacaBetaGraphGroupDataArray = z.array(
  RootCauseOaxacaBetaGraphGroupData,
);

export type RootCauseOaxacaBetaGraphGroupDataArray = z.infer<
  typeof RootCauseOaxacaBetaGraphGroupDataArray
>;

export const RootCauseOaxacaBetaLineChartLevelData = z
  .object({
    name: z.string(),
    trendline: RootCauseBinScatterGroupTrendline,
  })
  .describe('RootCauseOaxacaBetaLineChartLevelData');

export type RootCauseOaxacaBetaLineChartLevelData = z.infer<
  typeof RootCauseOaxacaBetaLineChartLevelData
>;

export const RootCauseOaxacaBetaLineChartData = z
  .object({
    control_variable: z.string(),
    treatment_level: RootCauseOaxacaBetaLineChartLevelData,
    reference_level: RootCauseOaxacaBetaLineChartLevelData,
  })
  .describe('RootCauseOaxacaBetaLineChartData');

export type RootCauseOaxacaBetaLineChartData = z.infer<
  typeof RootCauseOaxacaBetaLineChartData
>;

export const RootCauseOaxacaBetaGraphGroupPlotData = z
  .object({
    decompositionType: z.literal('Oaxaca Beta'),
    treatmentDimensionLevelColumnValue: z.string(),
    referenceDimensionLevelColumnValue: z.string(),
    data: z.union([
      z.array(RootCauseOaxacaBetaGraphGroupData),
      RootCauseOaxacaBetaLineChartData,
    ]),
  })
  .describe('RootCauseOaxacaBetaGraphGroupPlotData');

export type RootCauseOaxacaBetaGraphGroupPlotData = z.infer<
  typeof RootCauseOaxacaBetaGraphGroupPlotData
>;

export const RootCauseAddingVariablesGraphGroupResult = z
  .object({
    decompositionType: z.union([
      z.literal('Adding Variables'),
      z.literal('Oaxaca X'),
      z.literal('Oaxaca Beta'),
    ]),
    control_variable: z.string(),
    treatmentDimensionLevelColumnValue: z.string(),
    referenceDimensionLevelColumnValue: z.string(),
    plot_type: z.union([z.literal('barChart'), z.literal('lineChart')]),
    plotData: z.union([
      RootCauseOaxacaBetaGraphGroupPlotData,
      RootCauseAddingVariablesGraphPlotData,
    ]),
    controlVariableDisplayName: z.string(),
    outcomeMetricDisplayName: z.string(),
    outcomeMetricIsBoolean: z.boolean(),
    text: RootCauseGeneratedTextOutputs.nullish(),
    visualizationAttributes: RootCauseVisualizationAttributes.nullish(),
    explanation: z.union([z.string(), RootCauseGeneratedTextOutputs]).nullish(),
  })
  .describe('RootCauseAddingVariablesGraphGroupResult');

export type RootCauseAddingVariablesGraphGroupResult = z.infer<
  typeof RootCauseAddingVariablesGraphGroupResult
>;

export const RootCauseTextOnlyGroupResult = z
  .object({
    plot_type: z.literal('TextOnly'),
    control_variable: z.string(),
    beta_difference: z.number().nullish(),
    disparity_difference: z.number().nullish(),
    explanation: RootCauseGeneratedTextOutputs,
    visualizationAttributes: RootCauseVisualizationAttributes.nullish(),
  })
  .describe('RootCauseTextOnlyGroupResult');

export type RootCauseTextOnlyGroupResult = z.infer<
  typeof RootCauseTextOnlyGroupResult
>;

export const isDiversionBarGraphGroupResult = (
  result: RootCauseGroupResult,
): result is RootCauseDivergedBarGraphGroupResult => {
  return result.plot_type === 'DivergedBarGraph';
};

export const RootCauseBoxPlotThresholdDetails = z
  .object({
    threshold: z.number(),
    below_threshold_value: z.number(),
    above_threshold_value: z.number(),
  })
  .describe('RootCauseBoxPlotThresholdDetails');

export type RootCauseBoxPlotThresholdDetails = z.infer<
  typeof RootCauseBoxPlotThresholdDetails
>;

export const RootCauseBoxPlotGraphResult = z
  .object({
    plot_type: z.literal('BoxPlot'),
    x_min: z.number(),
    x_max: z.number(),
    treatment_threshold: z.number(),
    reference_threshold: z.number(),
    treatment_threshold_details: RootCauseBoxPlotThresholdDetails,
    reference_threshold_details: RootCauseBoxPlotThresholdDetails,
    control_variable: z.string(),
    text: z.null(),
    explanation: RootCauseGeneratedTextOutputs,
    visualizationAttributes: RootCauseVisualizationAttributes,
  })
  .describe('RootCauseBoxPlotGraphResult');

export type RootCauseBoxPlotGraphResult = z.infer<
  typeof RootCauseBoxPlotGraphResult
>;

export const RootCauseGroupResult = z
  .union([
    RootCauseBinScatterControlledVsUncontrolledGroupResult,
    RootCauseDivergedBarGraphGroupResult,
    RootCauseGenericBarGraphGroupResult,
    RootCauseTextOnlyGroupResult,
    RootCauseAddingVariablesGraphGroupResult,
    RootCauseBoxPlotGraphResult,
  ])
  .describe('RootCauseGroupResult');

export type RootCauseGroupResult = z.infer<typeof RootCauseGroupResult>;

/**
 * TODO refactor this entire type to be simpler for UI and camelCased
 * Keeping as-is to meet short deadline. We'll need to refactor this in Python to match this type after shipping
 */

export const FocusAreaPlotSummary = z
  .object({
    dimension: z.string(),
    metric: z.string(),
    referenceLevel: z.string(),
    treatmentLevel: z.string(),
    rootCauseGroupResult: RootCauseGroupResult,
  })
  .describe('FocusAreaPlotSummary');

export type FocusAreaPlotSummary = z.infer<typeof FocusAreaPlotSummary>;

export const FocusAreaPlotsSummary = z
  .object({
    summaries: z.array(FocusAreaPlotSummary),
  })
  .describe('FocusAreaPlotsSummary');

export type FocusAreaPlotsSummary = z.infer<typeof FocusAreaPlotsSummary>;

export const RootCausePlotsSummary = z
  .record(FocusArea, FocusAreaPlotsSummary)
  .describe('RootCausePlotsSummary');

export type RootCausePlotsSummary = z.infer<typeof RootCausePlotsSummary>;

export const generateTextFromRootCauseTextOutputs = ({
  textOutputs,
  focusAreaDefinition,
  agentReference, // applicants, employees, or suspects
  includeHtml = true,
}: {
  textOutputs: RootCauseGeneratedTextOutputs;
  focusAreaDefinition: MyFocusAreaDefinition;
  agentReference: string;
  includeHtml?: boolean;
}): string => {
  const formattedDiff = textOutputs.diff.toPrecision(3); // Equivalent to the .3g part
  const diffString = formattedDiff.padStart(5).trim();

  const controlVarText =
    textOutputs.variableToOutputMap[
      textOutputs.controlVariable
    ].toLocaleLowerCase();

  const preferredMetricDefinition =
    focusAreaDefinition.metrics.find((metric) => metric.isPreferred) ??
    focusAreaDefinition.metrics.at(0);

  if (!preferredMetricDefinition) {
    return '';
  }

  const isDiffGreaterThanZero = textOutputs.diff > 0;
  const isFocusAreaMetricNegative =
    preferredMetricDefinition.calibrationFraming === 'Negative';

  let change: string;
  if (isDiffGreaterThanZero === isFocusAreaMetricNegative) {
    change = 'increases';
  } else {
    change = 'decreases';
  }

  const focusAreaDisplayName =
    focusAreaDefinition.displayName.toLocaleLowerCase();

  const referenceLevel = textOutputs.referenceLevel.toLocaleLowerCase();
  const treatmentLevel = textOutputs.treatmentLevel.toLocaleLowerCase();
  const agentReferenceLower = agentReference.toLocaleLowerCase();

  let finalText: string;
  if (textOutputs.analysisType === 'Adding Variables') {
    finalText = `<strong>For ${treatmentLevel}</strong> ${agentReferenceLower}, accounting for <strong>${controlVarText}</strong> changes disparities in <strong>${focusAreaDisplayName} by ${diffString}%</strong>.`;
  } else if (textOutputs.analysisType === 'Oaxaca X') {
    finalText = `<strong>Differences in ${controlVarText}</strong> between ${treatmentLevel} ${agentReferenceLower} and ${referenceLevel} ${agentReferenceLower} <strong>explains ${diffString}% of their unadjusted gap in ${focusAreaDisplayName}</strong>.`;
  } else if (textOutputs.analysisType === 'Oaxaca Beta') {
    finalText = `<strong>${upperFirst(controlVarText.trim())} seems to influence ${focusAreaDisplayName}</strong> decisions differently for ${treatmentLevel} ${agentReferenceLower} in comparison to ${referenceLevel} ${agentReferenceLower}.`;
  } else if (textOutputs.analysisType === 'Disparity Over Skills') {
    finalText = `There is evidence to show that <strong>disparities in ${focusAreaDisplayName}</strong> between ${treatmentLevel} and ${referenceLevel} ${agentReferenceLower} <strong>${change} with ${controlVarText}</strong>.`;
  } else {
    return '';
  }

  if (includeHtml) {
    return finalText;
  } else {
    return finalText.replace(/<[^>]*>?/gm, '');
  }
};

export const extractRootCauseTextOutputs = (
  groupResult: RootCauseGroupResult | null,
): RootCauseGeneratedTextOutputs | string | null => {
  if (!groupResult) {
    return null;
  }

  if (isDiversionBarGraphGroupResult(groupResult)) {
    if (groupResult.text !== undefined && groupResult.text !== null) {
      return groupResult.text;
    } else {
      return null;
    }
  }

  if (groupResult.plot_type === 'BinScatterControlledVsUncontrolled') {
    return groupResult.explanation;
  }

  if (groupResult.plot_type === 'TextOnly') {
    return groupResult.explanation;
  }

  if (['barChart', 'lineChart'].includes(groupResult.plot_type)) {
    const localGroupResult =
      RootCauseAddingVariablesGraphGroupResult.parse(groupResult);
    return {
      analysisType: localGroupResult.decompositionType,
      controlVariable: localGroupResult.control_variable,
      variableToOutputMap: {
        [localGroupResult.control_variable]:
          localGroupResult.controlVariableDisplayName,
      },
      diff:
        'disparityDelta' in localGroupResult.plotData
          ? localGroupResult.plotData.disparityDelta
          : 0,
      treatmentLevel: localGroupResult.treatmentDimensionLevelColumnValue,
      referenceLevel: localGroupResult.referenceDimensionLevelColumnValue,
    };
  }

  if (
    'text' in groupResult &&
    groupResult.text !== undefined &&
    groupResult.text !== null
  ) {
    return groupResult.text;
  } else if (
    'explanation' in groupResult &&
    groupResult.explanation !== undefined &&
    groupResult.explanation !== null
  ) {
    return groupResult.explanation;
  } else {
    return null;
  }
};

export const yAxisDivergedBarGraph = (
  focusArea: MyFocusAreaDefinition,
): string => {
  return `${focusArea.displayName} Disparity`;
};

export const RootCauseGraphPartType = z.enum([
  'root-cause',
  'bias-test-visualization',
]);

export type RootCauseGraphPartType = z.infer<typeof RootCauseGraphPartType>;

export const RootCauseGraphPartSerializableProps = z.object({
  focusArea: MyFocusAreaDefinition,
  metricDefinition: MyMetricDefinition,
  performanceMetricDefinition: MyMetricDefinition.nullable(),
  activeGroupResult: RootCauseGroupResult,
  treatmentLevel: z.string(),
  referenceLevel: z.string(),
  dimension: z.string(),
  type: RootCauseGraphPartType,
  graphClassName: z.string(),
  hideDownload: z.boolean().optional(),
  calibrationFraming: CalibrationFraming.optional(),
});

export type RootCauseGraphPartSerializableProps = z.infer<
  typeof RootCauseGraphPartSerializableProps
>;

export const getUniqueGroupsByNameFromRootCauseGroupResult = (
  groupResult: RootCauseGroupResult,
) => {
  const parsedResult =
    RootCauseAddingVariablesGraphGroupResult.safeParse(groupResult);

  if (parsedResult.success && groupResult.plot_type === 'barChart') {
    return getLevelPairsFromResult(parsedResult.data).reverse();
  }

  return undefined;
};

export const getDisplayComparisonGroupNoteFromRootCauseGroupResult = (
  groupResult: RootCauseGroupResult,
) => {
  const isOaxacaBetaChart =
    'decompositionType' in groupResult &&
    groupResult.decompositionType === 'Oaxaca Beta';
  const isBarChart = groupResult.plot_type === 'barChart';

  return isOaxacaBetaChart && isBarChart;
};

export const getControlVariableNameFromRootCauseOutputs = (
  rootCauseOutputs: RootCauseGeneratedTextOutputs | string | null,
) => {
  return rootCauseOutputs && typeof rootCauseOutputs !== 'string'
    ? upperFirst(
        rootCauseOutputs.variableToOutputMap[
          rootCauseOutputs.controlVariable
        ] ?? '',
      )
    : null;
};

export const getBiasTestIdFromRootCauseGroupResult = (
  groupResult: RootCauseGroupResult | null,
) => {
  if (!groupResult) {
    return undefined;
  }

  const rootCauseOutputs = extractRootCauseTextOutputs(groupResult);

  if (!rootCauseOutputs || typeof rootCauseOutputs === 'string') {
    return undefined;
  }

  return rootCauseOutputs.biasTestId;
};

export const getDeepDiveAnalysisTypeFromRootCauseOutputs = ({
  rootCauseOutputs,
}: {
  rootCauseOutputs: RootCauseGeneratedTextOutputs | string | null;
}) => {
  return rootCauseOutputs && typeof rootCauseOutputs !== 'string'
    ? rootCauseOutputs.analysisType
    : null;
};

const getResultLevelPairsForOaxacaBeta = (
  result: RootCauseAddingVariablesGraphGroupResult,
): RootCauseGraphVariableBarPair[] => {
  const plotData = RootCauseOaxacaBetaGraphGroupPlotData.parse(result.plotData);

  const data = RootCauseOaxacaBetaGraphGroupDataArray.parse(plotData.data);

  const levelPairs =
    data?.flatMap((d) => {
      return [
        {
          ...d.referenceCategoryBarPair,
          name: `${d.referenceCategoryBarPair.name}*`,
        },
        d.treatmentCategoryBarPair,
      ];
    }) ?? [];

  const uniqueLevels = [...new Set(levelPairs.map((d) => d.name))];
  return uniqueLevels
    .map((name) => {
      return levelPairs.find((d) => d.name === name);
    })
    .filter((g) => g !== undefined);
};

const getResultLevelPairsForOaxacaX = (
  result: RootCauseAddingVariablesGraphGroupResult,
): RootCauseGraphVariableBarPair[] => {
  const plotData = RootCauseAddingVariablesGraphPlotData.parse(result.plotData);

  return [
    {
      ...plotData.withVariableBarPair,
      name: capitalizeFirstLetterInEachSentence(
        `${plotData.withVariableBarPair.name} ${result.controlVariableDisplayName}`,
      ),
    },
    {
      ...plotData.withoutVariableBarPair,
      name: capitalizeFirstLetterInEachSentence(
        `${plotData.withoutVariableBarPair.name} ${result.controlVariableDisplayName}`,
      ),
    },
  ];
};

/**
 * Return list of level pairs from result data. Ignore duplicates for oaxaca
 * beta.
 */
export const getLevelPairsFromResult = (
  result: RootCauseAddingVariablesGraphGroupResult | undefined,
): RootCauseGraphVariableBarPair[] => {
  switch (result?.decompositionType) {
    case 'Oaxaca Beta':
      return getResultLevelPairsForOaxacaBeta(result);
    case 'Oaxaca X':
    case 'Adding Variables':
      return getResultLevelPairsForOaxacaX(result);
    default:
      return [];
  }
};
